import { TextField } from '@mui/material';
import { enqueueSnackbar } from '~/utils/notifications';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { validateStructureSetLabel } from '~/utils/validation';

export default function StructureSetLabelField({
    structureSetLabel,
    onChange,
}: {
    structureSetLabel: string;
    onChange: (value: string) => void;
}) {
    const [newStructureSetLabel, setNewStructureSetLabel] =
        useState(structureSetLabel);
    const [validationError, setValidationError] = useState<string | null>(null);
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);

    useEffect(() => {
        if (validationError) {
            enqueueSnackbar(validationError, {
                variant: 'warning',
            });
            setValidationError(null);
        }
    }, [validationError]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewStructureSetLabel(e.target.value);
    };

    const handleEditingEnd = () => {
        const trimmedLabel = newStructureSetLabel.trim();
        const [isValid, errorMessage] = validateStructureSetLabel(trimmedLabel);
        if (!isValid) {
            setValidationError(errorMessage);
            setNewStructureSetLabel(structureSetLabel);
            return;
        } else {
            onChange(trimmedLabel);
        }
    };

    return (
        <TextField
            type="text"
            value={newStructureSetLabel}
            onChange={handleInputChange}
            onBlur={handleEditingEnd}
            autoFocus={showSensitiveInfo}
            label={<FormattedMessage id="label" />}
            variant="outlined"
            size="small"
            fullWidth
            data-testid="structure-set-label-field"
            // While patient info is hidden, blur only the entered value (not the
            // field chrome) and make it read-only; editing resumes once revealed.
            inputProps={{ readOnly: !showSensitiveInfo }}
            sx={{
                '& .MuiInputBase-input': {
                    filter: showSensitiveInfo ? 'none' : 'blur(0.5rem)',
                },
            }}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                    handleEditingEnd();
                }
            }}
        />
    );
}
